import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, Button, List, ListItem, ListItemButton, ListItemText, Divider, Alert, Checkbox, FormControlLabel, IconButton, Tooltip, Chip, Autocomplete, CircularProgress, FormControl, InputLabel, Select, MenuItem, Switch, Stack, Snackbar, Tabs, Tab, Paper } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROLES } from '../../../config/pagePermissions';
import { PHASES, usePhase } from '../../../contexts/PhaseContext';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import { onEvent } from '../../../services/socket';

export default function MessagesCenterPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const phaseCtx = usePhase();
  const me = phaseCtx?.user || null;
  const myRole = (me?.role || '').toString();
  const isSchoolAdmin = myRole === 'SchoolAdmin';
  const isAdminLevelRole = isSchoolAdmin || myRole === ROLES.PRINCIPAL || myRole === ROLES.HEAD_MASTER;
  const activePhaseCode = (phaseCtx?.activePhase || '').toString().trim().toLowerCase();
  const [messages, setMessages] = useState([]);
  const [toUserId, setToUserId] = useState('');
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientRole, setRecipientRole] = useState('');
  const [broadcast, setBroadcast] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{url, filename, size, mime}]
  const MAX_FILES = 5;
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [type, setType] = useState('inbox'); // inbox | sent
  const [activeTab, setActiveTab] = useState('inbox');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newMsgSnack, setNewMsgSnack] = useState({ open: false, subject: '', fromUserId: null });
  const [selectedMessageId, setSelectedMessageId] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const systemRoleSet = useMemo(() => new Set([
    ROLES.SYSTEM_ADMIN,
    ROLES.IT_MANAGER,
    ROLES.SALES_REP,
    ROLES.SUPPORT_AGENT,
  ]), []);

  const [assignableRoles, setAssignableRoles] = useState([]);
  const isParentRole = myRole === ROLES.PARENT;

  const resolveMyPhaseCode = () => {
    try {
      if (myRole === ROLES.PRINCIPAL) return PHASES.SEC;
      if (myRole === ROLES.HEAD_MASTER) return PHASES.NUR_PRI;
      // SchoolAdmin uses the currently-selected active phase
      if (isSchoolAdmin && activePhaseCode) return activePhaseCode;
      const pid = me?.phaseId;
      if (pid != null && pid !== '') {
        const raw = localStorage.getItem('phaseCatalog');
        const catalog = raw ? JSON.parse(raw) : [];
        const found = Array.isArray(catalog) ? catalog.find(p => String(p?.id) === String(pid)) : null;
        const code = (found?.code || '').toString().trim().toLowerCase();
        if (code) return code;
      }
      // Fallback for any role: use activePhaseCode from context
      if (activePhaseCode) return activePhaseCode;
    } catch (_) {}
    return '';
  };

  const myPhaseCode = resolveMyPhaseCode();
  const selectedRecipient = (recipientOptions || []).find(o => String(o?.id) === String(toUserId)) || null;

  useEffect(() => {
    let active = true;
    const activeSchoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
    const headers = {
      ...(activeSchoolId ? { 'X-Scope-School-Id': activeSchoolId } : {}),
      ...(activePhaseCode ? { 'X-Active-Phase': activePhaseCode } : {})
    };
    fetchJson('/api/messages/roles', { headers }).then(data => {
      if (!active) return;
      const list = Array.isArray(data) ? data : (data?.roles || []);
      // Server returns role name strings directly
      const names = list.map(r => (typeof r === 'string' ? r : r?.name)).filter(Boolean);
      setAssignableRoles(names);
    }).catch(() => {
      if (active) setAssignableRoles([]);
    });
    return () => { active = false; };
  }, [activePhaseCode]);

  const roleOptions = useMemo(() => {
    const base = assignableRoles.length > 0
      ? assignableRoles
      : Object.values(ROLES);
    return base
      .filter((r) => r !== ROLES.STUDENT && !systemRoleSet.has(r))
      .filter((r) => {
        // Parents cannot see/message SchoolAdmin
        if (r === ROLES.SCHOOL_ADMIN && (!isAdminLevelRole || isParentRole)) return false;
        return true;
      });
  }, [assignableRoles, isAdminLevelRole, isParentRole, systemRoleSet]);

  const setUrlParams = useCallback((updates, opts = {}) => {
    const params = new URLSearchParams(location.search);
    Object.entries(updates || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') params.delete(k);
      else params.set(k, String(v));
    });
    const qs = params.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: !!opts.replace });
  }, [location.pathname, location.search, navigate]);

  const closeDetail = useCallback(() => {
    setSelectedMessageId('');
    setSelectedMessage(null);
    setDetailError(null);
    setUrlParams({ messageId: '' }, { replace: true });
  }, [setUrlParams]);

  const load = useCallback(async (opts = {}) => {
    setError(null);
    setLoading(true);
    try {
      const effectiveType = (opts && opts.type) ? String(opts.type) : type;
      const params = new URLSearchParams();
      params.set('type', effectiveType);
      if (search) params.set('search', search);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (archived) params.set('archived', 'true');
      const res = await fetchJson(`/api/messages?${params.toString()}`);
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
    } catch (e) {
      setError(e?.message || t('messagesPage.errors.loadMessages'));
    } finally {
      setLoading(false);
    }
  }, [archived, dateFrom, dateTo, search, t, type]);

  const loadMessageDetail = useCallback(async (id, opts = {}) => {
    if (!id) return;
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await fetchJson(`/api/messages/${id}`);
      setSelectedMessage(res?.message || null);
      setSelectedMessageId(String(id));
      if (type === 'inbox') {
        try {
          await fetchJson(`/api/messages/${id}/read`, { method: 'PATCH' });
        } catch (_) {}
      }
      if (opts.updateUrl !== false) {
        setUrlParams({ messageId: id, tab: activeTab }, { replace: false });
      }
    } catch (e) {
      setSelectedMessage(null);
      setSelectedMessageId(String(id));
      setDetailError(e?.message || t('messagesPage.errors.loadMessage'));
    } finally {
      setDetailLoading(false);
      if (opts.refreshList !== false) {
        try {
          await load();
        } catch (_) {}
      }
    }
  }, [activeTab, load, setUrlParams, t, type]);

  useEffect(() => { load(); /* reload when filters change */ }, [activePhaseCode, load]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const messageId = params.get('messageId');
    if (tab === 'inbox' || tab === 'sent' || tab === 'compose') {
      setActiveTab(tab);
      if (tab === 'inbox' || tab === 'sent') setType(tab);
    }
    if (messageId) {
      if (String(messageId) !== String(selectedMessageId)) {
        setActiveTab('inbox');
        setType('inbox');
        loadMessageDetail(messageId, { updateUrl: false });
      }
    }
  }, [loadMessageDetail, location.search, selectedMessageId]);

  // Live updates: when a new message arrives, show banner and refresh inbox if viewing inbox
  useEffect(() => {
    let off = null;
    (async () => {
      off = await onEvent('message:new', async (payload) => {
        setNewMsgSnack({ open: true, subject: payload?.subject || t('messagesPage.newMessage'), fromUserId: payload?.fromUserId || null });
        if (type === 'inbox') {
          await load();
        }
      });
    })();
    return () => { if (typeof off === 'function') off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, t, type]);

  useEffect(() => {
    if (broadcast) {
      setRecipientOptions([]);
      return;
    }
    if (!recipientRole) return;
    let active = true;
    (async () => {
      setRecipientLoading(true);
      try {
        const data = await fetchJson(`/api/messages/recipients?query=&limit=500&role=${encodeURIComponent(recipientRole)}`);
        if (!active) return;
        setRecipientOptions(Array.isArray(data?.users) ? data.users : []);
      } catch (e) {
        if (!active) return;
        setRecipientOptions([]);
        setError(e?.message || t('messagesPage.errors.loadRecipients'));
      } finally {
        if (active) setRecipientLoading(false);
      }
    })();
    return () => { active = false; };
  }, [activePhaseCode, broadcast, recipientRole, t]);

  useEffect(() => {
    if (!isSchoolAdmin) return;
    // Avoid mixed-phase recipients when SchoolAdmin switches phases
    setToUserId('');
    setRecipientQuery('');
    // Do NOT clear recipientOptions here; it can race after a successful fetch and wipe the list.
    setRecipientRole((prev) => {
      if (!prev) return '';
      return roleOptions.includes(prev) ? prev : '';
    });
  }, [activePhaseCode, isSchoolAdmin, roleOptions]);

  useEffect(() => {
    if (broadcast) return;
    if (recipientRole) return;
    let timer = null;
    const run = async () => {
      if (!recipientQuery || recipientQuery.length < 2) {
        setRecipientOptions([]);
        return;
      }
      setRecipientLoading(true);
      try {
        const data = await fetchJson(`/api/messages/recipients?query=${encodeURIComponent(recipientQuery)}&limit=10`);
        setRecipientOptions(Array.isArray(data?.users) ? data.users : []);
      } catch (e) {
        setRecipientOptions([]);
        setError(e?.message || t('messagesPage.errors.loadRecipients'));
      } finally {
        setRecipientLoading(false);
      }
    };
    timer = setTimeout(run, 300);
    return () => { if (timer) clearTimeout(timer); };
  }, [broadcast, recipientQuery, recipientRole, t]);

  const send = async () => {
    setError(null);
    setInfo(null);
    try {
      if (broadcast) {
        if (!recipientRole) throw new Error(t('messagesPage.errors.selectRoleToBroadcast'));
        await fetchJson('/api/messages/broadcast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: recipientRole, subject: subject || null, content, attachments: attachments.map(a => a.url) })
        });
      } else {
        if (!toUserId) throw new Error(t('messagesPage.errors.selectRecipient'));
        await fetchJson('/api/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ toUserId: Number(toUserId), subject: subject || null, content, attachments: attachments.map(a => a.url) })
        });
      }
      setContent('');
      setSubject('');
      setToUserId('');
      setRecipientRole('');
      setAttachments([]);
      setInfo(t('messagesPage.snack.sent'));
      setSelectedMessageId('');
      setSelectedMessage(null);
      setActiveTab('sent');
      setType('sent');
      setUrlParams({ tab: 'sent', messageId: '' }, { replace: true });
      await load({ type: 'sent' });
    } catch (e) {
      setError(e?.message || t('messagesPage.errors.sendMessage'));
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      setError(t('messagesPage.errors.maxAttachments', { count: MAX_FILES }));
      return;
    }
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        setError(t('messagesPage.errors.fileExceedsLimit', { name: f.name, size: Math.round(MAX_SIZE / (1024 * 1024)) }));
        return;
      }
    }
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const data = await fetchJson('/api/messages/attachment', { method: 'POST', body: form });
        uploaded.push(data);
      }
      setAttachments(prev => [...prev, ...uploaded]);
      e.target.value = '';
    } catch (err) {
      setError(err?.message || t('messagesPage.errors.uploadFailed'));
    }
  };

  const markRead = async (id) => {
    try {
      await fetchJson(`/api/messages/${id}/read`, { method: 'PATCH' });
      await load();
    } catch (_) {}
  };

  const toggleArchive = async (id, value) => {
    try {
      await fetchJson(`/api/messages/${id}/archive`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ archived: !!value }) });
      await load();
    } catch (_) {}
  };

  const remove = async (id) => {
    try {
      await fetchJson(`/api/messages/${id}`, { method: 'DELETE' });
      await load();
    } catch (_) {}
  };

  const handleTabChange = (_, v) => {
    if (!v) return;
    setActiveTab(v);
    if (v === 'inbox' || v === 'sent') {
      setType(v);
      setUrlParams({ tab: v, messageId: '' }, { replace: true });
      setSelectedMessageId('');
      setSelectedMessage(null);
    } else if (v === 'compose') {
      setUrlParams({ tab: v, messageId: '' }, { replace: true });
      setSelectedMessageId('');
      setSelectedMessage(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" gutterBottom>{t('messagesPage.title')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Snackbar
        open={!!info}
        autoHideDuration={2500}
        onClose={() => setInfo(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setInfo(null)} sx={{ width: '100%' }}>
          {info}
        </Alert>
      </Snackbar>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab value="inbox" label={t('messagesPage.inbox')} />
        <Tab value="sent" label={t('messagesPage.sent')} />
        <Tab value="compose" label={t('messagesPage.compose')} />
      </Tabs>

      {activeTab === 'compose' && (
        <>
          <Box mb={2}>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="flex-start">
              <FormControlLabel
                control={
                  <Switch
                    checked={broadcast}
                    onChange={(_, v) => {
                      setBroadcast(v);
                      if (v) {
                        setToUserId('');
                        setRecipientQuery('');
                        setRecipientOptions([]);
                      }
                    }}
                  />
                }
                label={t('messagesPage.broadcastToRole')}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="recipient-role-label">{t('messagesPage.role')}</InputLabel>
                <Select
                  labelId="recipient-role-label"
                  label={t('messagesPage.role')}
                  value={recipientRole}
                  onChange={(e) => {
                    setRecipientRole(e.target.value);
                    setRecipientQuery('');
                    setToUserId('');
                  }}
                >
                  <MenuItem value=""><em>{t('messagesPage.allRoles')}</em></MenuItem>
                  {roleOptions.map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!broadcast && (
                <Autocomplete
                  options={recipientOptions}
                  loading={recipientLoading}
                  value={selectedRecipient}
                  inputValue={recipientQuery}
                  openOnFocus
                  filterOptions={(opts) => opts}
                  noOptionsText={recipientLoading ? t('messagesPage.loading') : t('messagesPage.noRecipients')}
                  loadingText={t('messagesPage.loading')}
                  isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                  getOptionLabel={(opt) => {
                    if (!opt) return '';
                    if (typeof opt === 'string') return opt;
                    const name = (opt.name || '').toString();
                    const role = opt.role || opt.roleCode || '';
                    const subjects = Array.isArray(opt.subjects) && opt.subjects.length ? opt.subjects.join(', ') : '';
                    const studentNames = Array.isArray(opt.studentNames) && opt.studentNames.length ? opt.studentNames.join(', ') : '';
                    if (studentNames) return `${name} (${studentNames})`;
                    if (subjects) return `${name} (${role} – ${subjects})`;
                    if (role) return `${name} (${role})`;
                    return name;
                  }}
                  onInputChange={(_, v) => setRecipientQuery(v)}
                  onChange={(_, v) => setToUserId(v?.id ? String(v.id) : '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('messagesPage.recipient')}
                      size="small"
                      sx={{ minWidth: 260 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {recipientLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Box>

            <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: 420 } }}>
              <TextField
                label={t('messagesPage.subject')}
                value={subject}
                onChange={e => setSubject(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />

              <TextField
                label={t('messagesPage.message')}
                value={content}
                onChange={e => setContent(e.target.value)}
                size="small"
                multiline
                minRows={4}
                fullWidth
              />

              <Stack direction="row" spacing={2} alignItems="center" mt={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" component="label">{t('messagesPage.attachFiles')}
                  <input hidden multiple type="file" onChange={onUpload} />
                </Button>
                <Typography variant="caption" color="text.secondary">{t('messagesPage.maxFilesHint', { count: MAX_FILES, size: Math.round(MAX_SIZE / (1024 * 1024)) })}</Typography>
              </Stack>

              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button variant="contained" onClick={send}>{t('messagesPage.send')}</Button>
              </Box>
            </Box>
          </Box>

          {attachments.length > 0 && (
            <Box mb={2} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', maxWidth: { xs: '100%', md: 420 } }}>
              {attachments.map((a, idx) => (
                <Chip key={`${a.url}-${idx}`} label={a.filename || a.url} onDelete={() => setAttachments(prev => prev.filter((_, i) => i !== idx))} clickable component="a" href={a.url} target="_blank" rel="noopener" />
              ))}
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={newMsgSnack.open}
        autoHideDuration={5000}
        onClose={() => setNewMsgSnack({ open: false, subject: '', fromUserId: null })}
        message={newMsgSnack.subject ? t('messagesPage.newMessageWithSubject', { subject: newMsgSnack.subject }) : t('messagesPage.newMessage')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {(activeTab === 'inbox' || activeTab === 'sent') && (
        <>
          {selectedMessageId && (
            <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ m: 0 }}>{selectedMessage?.subject || t('messagesPage.message')}</Typography>
                <Button size="small" onClick={closeDetail}>{t('messagesPage.backToList')}</Button>
              </Box>
              {detailError && <Alert severity="error" sx={{ mb: 1 }}>{detailError}</Alert>}
              {detailLoading ? (
                <Typography variant="body2" color="text.secondary">{t('messagesPage.loading')}</Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {(() => {
                      const fromVal = selectedMessage?.fromName || (selectedMessage?.fromUserId ? `#${selectedMessage.fromUserId}` : '');
                      return fromVal ? t('messagesPage.fromPrefix', { value: fromVal }) : '';
                    })()}
                    {(() => {
                      const isBc = selectedMessage?.isBroadcast === true;
                      const toLabel = isBc
                        ? (selectedMessage?.toUserRole || selectedMessage?.toName || (selectedMessage?.toUserId ? `#${selectedMessage.toUserId}` : ''))
                        : (selectedMessage?.toName || (selectedMessage?.toUserId ? `#${selectedMessage.toUserId}` : ''));
                      return toLabel ? `  •  ${t('messagesPage.toPrefix', { value: toLabel })}` : '';
                    })()}
                    {selectedMessage?.createdAt ? `  •  ${new Date(selectedMessage.createdAt).toLocaleString()}` : ''}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{(selectedMessage?.content || '').toString()}</Typography>
                  {(Array.isArray(selectedMessage?.attachments) && selectedMessage.attachments.length > 0) && (
                    <Box mt={2} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedMessage.attachments.map((u, idx) => (
                        <Chip key={`${u}-${idx}`} label={u} clickable component="a" href={u} target="_blank" rel="noopener" />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Paper>
          )}

          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField label={t('messagesPage.search')} value={search} onChange={e => setSearch(e.target.value)} size="small" />
            <TextField label={t('messagesPage.fromDate')} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            <TextField label={t('messagesPage.toDate')} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            <FormControlLabel control={<Checkbox checked={archived} onChange={e => setArchived(e.target.checked)} />} label={t('messagesPage.showArchived')} />
            <Tooltip title={t('messagesPage.applyFilters')}>
              <span>
                <IconButton color="primary" onClick={load} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <List>
            {messages.map((m) => (
              <React.Fragment key={m.id}>
                <ListItem
                  disablePadding
                  secondaryAction={
                    <Box>
                      {!m.readAt && (
                        <Tooltip title={t('messagesPage.tooltips.markRead')}>
                          <IconButton onClick={() => markRead(m.id)} size="small"><MarkEmailReadIcon /></IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={m.archived ? t('messagesPage.tooltips.unarchive') : t('messagesPage.tooltips.archive')}>
                        <IconButton onClick={() => toggleArchive(m.id, !m.archived)} size="small">{m.archived ? <UnarchiveIcon /> : <ArchiveIcon />}</IconButton>
                      </Tooltip>
                      <Tooltip title={t('messagesPage.tooltips.delete')}>
                        <IconButton onClick={() => remove(m.id)} size="small" color="error"><DeleteIcon /></IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => loadMessageDetail(m.id)} selected={String(m.id) === String(selectedMessageId)}>
                    <ListItemText
                      primary={<>
                        <Typography component="span" fontWeight={m.readAt ? 500 : 700}>{m.subject || t('messagesPage.message')}</Typography>
                        <Chip
                          size="small"
                          sx={{ ml: 1 }}
                          label={(() => {
                            if (type === 'sent') {
                              const isBc = m.isBroadcast === true;
                              const toLabel = isBc
                                ? (m.toUserRole || m.toName || (m.toUserId != null ? `#${m.toUserId}` : ''))
                                : (m.toName || (m.toUserId != null ? `#${m.toUserId}` : ''));
                              return toLabel ? t('messagesPage.toPrefix', { value: toLabel }) : t('messagesPage.to');
                            }
                            const fromLabel = m.fromName || (m.fromUserId != null ? `#${m.fromUserId}` : '');
                            return fromLabel ? t('messagesPage.fromPrefix', { value: fromLabel }) : t('messagesPage.from');
                          })()}
                        />
                      </>}
                      secondary={<>
                        <Typography component="span" variant="body2">{((m.content || '').toString()).slice(0, 140)}</Typography>
                        <Typography component="span" variant="caption" sx={{ display: 'block' }}>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</Typography>
                      </>}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
            {messages.length === 0 && (
              <ListItem><ListItemText primary={loading ? t('messagesPage.loading') : t('messagesPage.noMessagesFound')} /></ListItem>
            )}
          </List>
        </>
      )}
    </Box>
  );
}
